// SimilarOwnerWarning — on an EPMA owner card marked "New owner", after staff
// type the name: "Possible existing owner in AppFolio: Jane Doe (jane@…, Walla
// Walla) — <reason>" with Use this owner / It's a different owner (spec §13.1).
//
// The candidates come from GET /api/owners/similar (fuzzy: name order, middle
// names, nicknames, couple listings, company suffixes). Which ones to show —
// and whether staff already dismissed them for this name — is decided in
// lib/ownerSync.ts (similarCandidatesFor); this only renders. Names, emails,
// cities and the server's reason; the AppFolio id is a React key only.

import { ownerMatchDetail, similarReasonText, type SimilarOwner } from '@/lib/ownerSync';

function CandidateLine({ c }: { c: SimilarOwner }): React.JSX.Element {
  const detail = ownerMatchDetail(c);
  const reason = similarReasonText(c);
  return (
    <>
      <strong>{c.displayName}</strong>
      {detail ? ` (${detail})` : ''}
      {reason ? ` — ${reason}` : ''}
    </>
  );
}

export function SimilarOwnerWarning({
  candidates,
  onUse,
  onDismiss,
}: {
  candidates: SimilarOwner[];
  onUse: (c: SimilarOwner) => void;
  onDismiss: () => void;
}): React.JSX.Element | null {
  if (candidates.length === 0) return null;
  const one = candidates.length === 1;
  return (
    <div className="alert" data-tone="warning" role="note" data-testid="similar-owner-warning">
      <div className="alert-body">
        {one ? (
          <div>
            Possible existing owner in AppFolio: <CandidateLine c={candidates[0]!} />
          </div>
        ) : (
          <div>Possible existing owners in AppFolio:</div>
        )}
        {one ? (
          <div style={{ display: 'flex', gap: 'var(--space-2)', marginTop: 'var(--space-2)', flexWrap: 'wrap' }}>
            <button type="button" className="btn" data-size="sm" onClick={() => onUse(candidates[0]!)}>
              Use this owner
            </button>
            <button type="button" className="btn" data-variant="secondary" data-size="sm" onClick={onDismiss}>
              It&apos;s a different owner
            </button>
          </div>
        ) : (
          <ul
            style={{
              listStyle: 'none',
              margin: 'var(--space-2) 0 0',
              padding: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--space-2)',
            }}
          >
            {candidates.map((c) => (
              <li
                key={c.appfolioOwnerId}
                style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexWrap: 'wrap' }}
              >
                <span style={{ flex: '1 1 auto' }}>
                  <CandidateLine c={c} />
                </span>
                <button type="button" className="btn" data-size="sm" onClick={() => onUse(c)}>
                  Use this owner
                </button>
              </li>
            ))}
            <li>
              <button type="button" className="btn" data-variant="secondary" data-size="sm" onClick={onDismiss}>
                It&apos;s a different owner
              </button>
            </li>
          </ul>
        )}
      </div>
    </div>
  );
}
